import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
  orderBy,
  DocumentData,
  QueryDocumentSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

// Types
export interface ContactSubmission {
  id?: string;
  name: string;
  email: string;
  reason: string;
  message: string;
  status: 'new' | 'inProgress' | 'responded' | 'closed';
  adminResponse?: string;
  createdAt: Date;
  updatedAt: Date;
  userId?: string;
  userEmail?: string;
  office?: string;
}

// Helper function to remove undefined values from an object
const removeUndefinedFields = (obj: Record<string, any>) => {
  const cleanObj: Record<string, any> = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      cleanObj[key] = obj[key];
    }
  });
  return cleanObj;
};

interface ContactState {
  submissions: ContactSubmission[];
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  currentSubmission: ContactSubmission | null;
}

type ContactAction =
  | { type: 'SET_SUBMISSIONS'; payload: ContactSubmission[] }
  | { type: 'ADD_SUBMISSION'; payload: ContactSubmission }
  | { type: 'UPDATE_SUBMISSION'; payload: ContactSubmission }
  | { type: 'SET_CURRENT_SUBMISSION'; payload: ContactSubmission | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ADMIN'; payload: boolean };

interface ContactContextType {
  state: ContactState;
  submitContactForm: (formData: Omit<ContactSubmission, 'id' | 'createdAt' | 'updatedAt' | 'status'>) => Promise<void>;
  getUserSubmissions: () => Promise<void>;
  getAllSubmissions: () => Promise<void>;
  respondToSubmission: (submissionId: string, response: string) => Promise<void>;
  checkIsAdmin: () => Promise<boolean>;
  clearError: () => void;
}

const initialState: ContactState = {
  submissions: [],
  loading: false,
  error: null,
  isAdmin: false,
  currentSubmission: null,
};

const ContactContext = createContext<ContactContextType | undefined>(undefined);

function contactReducer(state: ContactState, action: ContactAction): ContactState {
  switch (action.type) {
    case 'SET_SUBMISSIONS':
      return { ...state, submissions: action.payload, loading: false };
    case 'ADD_SUBMISSION':
      return {
        ...state,
        submissions: [...state.submissions, action.payload],
        loading: false,
      };
    case 'UPDATE_SUBMISSION':
      return {
        ...state,
        submissions: state.submissions.map(s =>
          s.id === action.payload.id ? action.payload : s
        ),
        loading: false,
      };
    case 'SET_CURRENT_SUBMISSION':
      return { ...state, currentSubmission: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_ADMIN':
      return { ...state, isAdmin: action.payload };
    default:
      return state;
  }
}

export function ContactProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(contactReducer, initialState);

  const checkIsAdmin = useCallback(async () => {
    const user = auth.currentUser;
    if (!user?.phoneNumber) return false;
    
    const isAdmin = ['+998991963315', '+998901234568'].includes(user.phoneNumber);
    dispatch({ type: 'SET_ADMIN', payload: isAdmin });
    return isAdmin;
  }, []);

  const submitContactForm = useCallback(async (formData: Omit<ContactSubmission, 'id' | 'createdAt' | 'updatedAt' | 'status'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      const currentDate = new Date();
      
      // Create submission data with optional user fields
      let submissionData: Omit<ContactSubmission, 'id'> = {
        ...formData,
        status: 'new',
        createdAt: currentDate,
        updatedAt: currentDate,
      };

      // Only add user-related fields if they exist
      if (user) {
        if (user.uid) {
          submissionData.userId = user.uid;
        }
        if (user.email) {
          submissionData.userEmail = user.email;
        }
      }

      // Remove any undefined fields before sending to Firestore
      const cleanSubmissionData = removeUndefinedFields({
        ...submissionData,
        createdAt: Timestamp.fromDate(currentDate),
        updatedAt: Timestamp.fromDate(currentDate),
      });

      const docRef = await addDoc(collection(db, 'contact_submissions'), cleanSubmissionData);

      dispatch({
        type: 'ADD_SUBMISSION',
        payload: { ...submissionData, id: docRef.id },
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to submit contact form' });
      throw error;
    }
  }, []);

  const getUserSubmissions = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const q = query(
        collection(db, 'contact_submissions'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const submissions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate(),
      })) as ContactSubmission[];

      dispatch({ type: 'SET_SUBMISSIONS', payload: submissions });
    } catch (error) {
      console.error('Error fetching user submissions:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch submissions' });
    }
  }, []);

  const getAllSubmissions = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      const q = query(
        collection(db, 'contact_submissions'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const submissions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate(),
      })) as ContactSubmission[];

      dispatch({ type: 'SET_SUBMISSIONS', payload: submissions });
    } catch (error) {
      console.error('Error fetching all submissions:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch submissions' });
    }
  }, [checkIsAdmin]);

  const respondToSubmission = useCallback(async (submissionId: string, response: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      const submissionRef = doc(db, 'contact_submissions', submissionId);
      const currentDate = new Date();

      await updateDoc(submissionRef, {
        adminResponse: response,
        status: 'responded',
        updatedAt: Timestamp.fromDate(currentDate),
      });

      const updatedSubmission = state.submissions.find(s => s.id === submissionId);
      if (updatedSubmission) {
        dispatch({
          type: 'UPDATE_SUBMISSION',
          payload: {
            ...updatedSubmission,
            adminResponse: response,
            status: 'responded',
            updatedAt: currentDate,
          },
        });
      }
    } catch (error) {
      console.error('Error responding to submission:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to respond to submission' });
    }
  }, [checkIsAdmin, state.submissions]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const value = {
    state,
    submitContactForm,
    getUserSubmissions,
    getAllSubmissions,
    respondToSubmission,
    checkIsAdmin,
    clearError,
  };

  return (
    <ContactContext.Provider value={value}>
      {children}
    </ContactContext.Provider>
  );
}

export function useContact() {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
}